/**
 * Frontend Error Parser
 * Extracts detailed error information from API responses
 */

export interface FieldError {
  field: string;
  message: string;
  value?: unknown;
}

export interface ParsedError {
  message: string;
  code?: string;
  statusCode?: number;
  fields?: FieldError[];
  context?: Record<string, unknown>;
  timestamp?: string;
  originalError?: unknown;
}

/**
 * Validate if an unknown value is a FieldError
 */
function isFieldError(value: unknown): value is FieldError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'field' in value &&
    'message' in value &&
    typeof (value as FieldError).field === 'string' &&
    typeof (value as FieldError).message === 'string'
  );
}

/**
 * Safely validate and extract FieldError array
 */
function validateFieldErrors(fields: unknown): FieldError[] | undefined {
  if (!Array.isArray(fields)) return undefined;

  const validFields = fields.filter(isFieldError);
  return validFields.length > 0 ? validFields : undefined;
}

/**
 * Parse error from API response
 * Handles multiple error formats:
 * 1. Structured API errors with code, fields, context
 * 2. Simple { error: "message" } responses
 * 3. Error objects from fetch/network failures
 * 4. Error strings from apiRequest
 */
export function parseApiError(error: unknown): ParsedError {
  // Handle null/undefined
  if (!error) {
    return {
      message: 'An unknown error occurred',
      originalError: error,
    };
  }

  // If error is already a parsed error object with our structure
  const errorObj = error as Record<string, unknown>;
  if (errorObj.error && typeof errorObj.error === 'string') {
    return {
      message: errorObj.error,
      code: typeof errorObj.code === 'string' ? errorObj.code : undefined,
      statusCode: typeof errorObj.statusCode === 'number' ? errorObj.statusCode : undefined,
      fields: validateFieldErrors(errorObj.fields),
      context:
        typeof errorObj.context === 'object' && errorObj.context !== null
          ? (errorObj.context as Record<string, unknown>)
          : undefined,
      timestamp: typeof errorObj.timestamp === 'string' ? errorObj.timestamp : undefined,
      originalError: error,
    };
  }

  // Handle Error objects from apiRequest
  // Format: "401: {"error":"Invalid credentials"}"
  if (errorObj.message && typeof errorObj.message === 'string') {
    // Try to extract status code and JSON body
    const match = errorObj.message.match(/^(\d+):\s*(\{.+\})$/);

    if (match) {
      const statusCode = parseInt(match[1], 10);
      try {
        const body = JSON.parse(match[2]) as Record<string, unknown>;
        return {
          message:
            typeof body.error === 'string'
              ? body.error
              : typeof body.message === 'string'
                ? body.message
                : errorObj.message,
          code: typeof body.code === 'string' ? body.code : undefined,
          statusCode:
            statusCode || (typeof body.statusCode === 'number' ? body.statusCode : undefined),
          fields: validateFieldErrors(body.fields),
          context:
            typeof body.context === 'object' && body.context !== null
              ? (body.context as Record<string, unknown>)
              : undefined,
          timestamp: typeof body.timestamp === 'string' ? body.timestamp : undefined,
          originalError: error,
        };
      } catch {
        // JSON parse failed, use raw message
      }
    }

    // Plain error message
    return {
      message: errorObj.message,
      originalError: error,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      originalError: error,
    };
  }

  // Fallback for unknown error types
  return {
    message: 'An unexpected error occurred',
    originalError: error,
  };
}

/**
 * Get a user-friendly error message
 * Prioritizes field-specific errors, then general message
 */
export function getErrorMessage(error: unknown): string {
  const parsed = parseApiError(error);

  // If there are field errors, show the first one
  if (parsed.fields && parsed.fields.length > 0) {
    const firstFieldError = parsed.fields[0];
    return `${firstFieldError.field}: ${firstFieldError.message}`;
  }

  return parsed.message;
}

/**
 * Get field-specific error for form validation
 */
export function getFieldError(error: unknown, fieldName: string): string | undefined {
  const parsed = parseApiError(error);

  if (!parsed.fields) return undefined;

  const fieldError = parsed.fields.find(f => f.field === fieldName);
  return fieldError?.message;
}

/**
 * Get all field errors as a map for form handling
 */
export function getFieldErrors(error: unknown): Record<string, string> {
  const parsed = parseApiError(error);

  if (!parsed.fields) return {};

  return parsed.fields.reduce(
    (acc, fieldError) => {
      acc[fieldError.field] = fieldError.message;
      return acc;
    },
    {} as Record<string, string>,
  );
}

/**
 * Format error for detailed display
 * Includes all available context
 */
export function formatDetailedError(error: unknown): string {
  const parsed = parseApiError(error);

  let {message} = parsed;

  // Add error code if available
  if (parsed.code) {
    message += ` (${parsed.code})`;
  }

  // Add field errors
  if (parsed.fields && parsed.fields.length > 0) {
    const fieldMessages = parsed.fields.map(f => `  • ${f.field}: ${f.message}`).join('\n');
    message += `\n\nField errors:\n${fieldMessages}`;
  }

  // Add context if available
  if (parsed.context && Object.keys(parsed.context).length > 0) {
    const contextEntries = Object.entries(parsed.context)
      .map(([key, value]) => `  • ${key}: ${JSON.stringify(value)}`)
      .join('\n');
    message += `\n\nAdditional info:\n${contextEntries}`;
  }

  return message;
}
